import { marked } from 'marked';
import { BodyImage } from './models/app-release';

/**
 * Markdown rendering shared by everything that shows release notes: the
 * release-notes modal, the inbox item, the compose preview.
 *
 * Same marked configuration as the Markdown editor (see the rationale in
 * markdown-editor.component.ts): headerIds/mangle off so v4 emits what the
 * newer marked on picaluna-v3 emits. Keep the two in step.
 */
const MARKED_OPTIONS: marked.MarkedOptions = { headerIds: false, mangle: false };

/** A slide break is a line that is exactly `---` (mirrors AppRelease.slides server-side). */
const SLIDE_SEPARATOR_RE = /^\s*---\s*$/m;

/** In-body image marker written by the Markdown editor: `![alt](conductor-file:1234)`. */
const BODY_IMAGE_RE = /!\[([^\]]*)\]\(conductor-file:(\d+)\)/g;

export function renderMarkdown(md: string): string {
  return marked.parse(md || '', MARKED_OPTIONS) as string;
}

export function splitSlides(md: string): string[] {
  return (md || '')
    .split(SLIDE_SEPARATOR_RE)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

const VIDEO_EXT_RE = /\.(mp4|m4v|webm|mov)(?=$|[?#])/i;

/** Video by MIME type when the server told us, else by the file extension in the S3 key. */
export function isVideoMedia(url: string, type?: string): boolean {
  if (type) return type.startsWith('video/');
  return VIDEO_EXT_RE.test((url || '').split('?')[0]);
}

export interface MediaRenderOptions {
  /** Videos start on their own, muted and looping — for a slide that IS the demo. */
  autoplayVideo?: boolean;
}

/** Raw HTML for a video marker; marked passes an HTML block through untouched. */
export function videoHtml(url: string, alt: string, options: MediaRenderOptions = {}): string {
  const safeUrl = url.replace(/"/g, '&quot;');
  const safeAlt = (alt || 'Video').replace(/"/g, '&quot;');
  const auto = options.autoplayVideo ? ' autoplay muted loop' : '';
  return `\n\n<video controls playsinline preload="metadata"${auto} src="${safeUrl}" aria-label="${safeAlt}"></video>\n\n`;
}

/**
 * Swap `conductor-file:<id>` markers for the signed URLs the server resolved.
 * Images stay Markdown images; a video (mp4) becomes a <video> block. A marker
 * with no URL (deleted file, S3 down) is dropped rather than left broken.
 */
export function resolveBodyImages(
  md: string,
  images: BodyImage[] | undefined,
  options: MediaRenderOptions = {}
): string {
  const byId = new Map<number, BodyImage>((images || []).map((i) => [i.fileId, i]));
  return (md || '').replace(BODY_IMAGE_RE, (whole, alt: string, id: string) => {
    const media = byId.get(Number(id));
    if (!media || !media.url) return '';
    return isVideoMedia(media.url, media.type)
      ? videoHtml(media.url, alt, options)
      : `![${alt}](${media.url})`;
  });
}

/** For summaries (the inbox item): text only, no image markers. */
export function stripBodyImages(md: string): string {
  return (md || '').replace(BODY_IMAGE_RE, '').trim();
}

/** Every file id referenced by an in-body image marker, de-duplicated. */
export function bodyImageFileIds(md: string): number[] {
  const ids = new Set<number>();
  for (const m of (md || '').matchAll(BODY_IMAGE_RE)) {
    const id = Number(m[2]);
    if (Number.isInteger(id) && id > 0) ids.add(id);
  }
  return [...ids];
}
